/**
 * Battleship Game Engine
 * Standard 10x10 grid, 5 naval vessels, fog of war, hit/sunk evaluation
 */

export const GRID_SIZE = 10;

export const SHIPS = [
  { id: 'carrier', name: 'Carrier', size: 5, icon: '🚢' },
  { id: 'battleship', name: 'Battleship', size: 4, icon: '🛳️' },
  { id: 'cruiser', name: 'Cruiser', size: 3, icon: '🛥️' },
  { id: 'submarine', name: 'Submarine', size: 3, icon: '🦈' },
  { id: 'destroyer', name: 'Destroyer', size: 2, icon: '🚤' }
];

export const CELL_WATER = 0;
export const CELL_SHIP = 1;
export const CELL_MISS = 2;
export const CELL_HIT = 3;

export class BattleshipEngine {
  constructor() {
    this.reset();
  }

  reset() {
    this.ships = []; // [{ id, name, size, x, y, horizontal, hits: 0, sunk: false, coords: [{x, y}] }]
    this.shotsReceived = new Map(); // key "x,y" => { hit: boolean, sunk: string|null }
    this.shotsFired = new Map();    // key "x,y" => { hit: boolean, sunk: string|null }
    this.isFleetPlaced = false;
  }

  canPlaceShip(shipId, size, startX, startY, horizontal, currentShips = this.ships) {
    const coords = [];
    for (let i = 0; i < size; i++) {
      const x = horizontal ? startX + i : startX;
      const y = horizontal ? startY : startY + i;

      if (x < 0 || x >= GRID_SIZE || y < 0 || y >= GRID_SIZE) {
        return { valid: false, coords: [] };
      }
      coords.push({ x, y });
    }

    // Check overlap with other already placed ships
    for (const ship of currentShips) {
      if (ship.id === shipId) continue;
      for (const c of coords) {
        if (ship.coords.some((sc) => sc.x === c.x && sc.y === c.y)) {
          return { valid: false, coords: [] };
        }
      }
    }

    return { valid: true, coords };
  }

  placeShip(shipDef, x, y, horizontal) {
    const check = this.canPlaceShip(shipDef.id, shipDef.size, x, y, horizontal);
    if (!check.valid) return false;

    // Remove existing if already placed
    this.ships = this.ships.filter((s) => s.id !== shipDef.id);

    this.ships.push({
      id: shipDef.id,
      name: shipDef.name,
      size: shipDef.size,
      x,
      y,
      horizontal,
      hits: 0,
      sunk: false,
      coords: check.coords
    });

    this.isFleetPlaced = this.ships.length === SHIPS.length;
    return true;
  }

  generateRandomFleet() {
    this.ships = [];
    for (const def of SHIPS) {
      let placed = false;
      let attempts = 0;
      while (!placed && attempts < 100) {
        attempts++;
        const horizontal = Math.random() < 0.5;
        const maxX = horizontal ? GRID_SIZE - def.size : GRID_SIZE - 1;
        const maxY = horizontal ? GRID_SIZE - 1 : GRID_SIZE - def.size;
        const x = Math.floor(Math.random() * (maxX + 1));
        const y = Math.floor(Math.random() * (maxY + 1));

        if (this.placeShip(def, x, y, horizontal)) {
          placed = true;
        }
      }
    }
    this.isFleetPlaced = this.ships.length === SHIPS.length;
    return this.isFleetPlaced;
  }

  receiveShot(x, y) {
    const key = `${x},${y}`;
    if (this.shotsReceived.has(key)) {
      return { duplicate: true };
    }

    let hitShip = null;
    for (const ship of this.ships) {
      if (ship.coords.some((c) => c.x === x && c.y === y)) {
        hitShip = ship;
        break;
      }
    }

    const hit = !!hitShip;
    let sunkName = null;

    if (hitShip) {
      hitShip.hits++;
      if (hitShip.hits >= hitShip.size) {
        hitShip.sunk = true;
        sunkName = hitShip.name;
      }
    }

    const allSunk = this.ships.every((s) => s.sunk);
    const result = { hit, sunk: sunkName, allSunk, x, y };
    this.shotsReceived.set(key, result);
    return result;
  }

  recordFiredShot(x, y, hit, sunkName, allSunk = false) {
    const key = `${x},${y}`;
    this.shotsFired.set(key, { hit, sunk: sunkName, allSunk, x, y });
  }

  hasFiredAt(x, y) {
    return this.shotsFired.has(`${x},${y}`);
  }

  getSunkShips() {
    return this.ships.filter((s) => s.sunk);
  }

  allShipsSunk() {
    return this.ships.length === SHIPS.length && this.ships.every((s) => s.sunk);
  }

  /**
   * Sanitized state for multiplayer sync without leaking hidden ship positions
   */
  getSanitizedPublicState() {
    return {
      shotsFired: Array.from(this.shotsFired.entries()),
      shotsReceived: Array.from(this.shotsReceived.entries()),
      sunkCount: this.getSunkShips().length
    };
  }
}
